import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineBlock from '~icons/ic/baseline-block'
import { executeSetBlockState } from 'src/services/apollo/user'
import { UIButton } from '../ui/Button'
import MarkdownRenderer from '../ui/MarkdownRenderer'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from '../ui/Modal'

export type BlockConfirmDialogProps = {
  type: 'block' | 'unblock'
  targetUserId: string
  targetUsername: string
  onComplete?: (choice: 'cancel' | 'confirm') => void
}

export const BlockConfirmDialog = ({
  type,
  targetUserId,
  targetUsername,
  onComplete,
}: BlockConfirmDialogProps) => {
  const { t } = useTranslation(['common', 'action'])
  const [processing, setProcessing] = useState(false)

  const handleCancel = () => {
    onComplete?.('cancel')
  }

  const handleConfirm = async () => {
    try {
      setProcessing(true)
      await executeSetBlockState({
        userId: targetUserId,
        target: type === 'block',
      })
      onComplete?.('confirm')
    } catch (e) {
      const err = e as Error
      toast.error(err.message)
    } finally {
      setProcessing(false)
    }
  }

  return (
    <>
      <UIDialogHeader
        heading={t(`common:block.confirm.heading.${type}`, {
          username: targetUsername,
        })}
      />

      <UIDialogContent>
        <MarkdownRenderer className="prose prose-sm sm:prose-base prose-neutral dark:prose-invert">
          {t(`common:block.confirm.content.${type}`, {
            username: targetUsername,
          })}
        </MarkdownRenderer>
      </UIDialogContent>

      <UIDialogFooter>
        <UIButton
          variant="filled"
          color="danger"
          onPress={handleConfirm}
          busy={processing}
          icon={<IcBaselineBlock className="mx-2 size-5" />}
        >
          {t(`common:block.confirm.action.${type}`)}
        </UIButton>

        <UIButton onPress={handleCancel} busy={processing}>
          {t('action:cancel')}
        </UIButton>
      </UIDialogFooter>
    </>
  )
}

export const dialogBlockConfirm = (
  type: 'block' | 'unblock',
  targetUserId: string,
  targetUsername: string,
  onComplete?: (choice: 'cancel' | 'confirm') => void,
) => {
  return UIDialog.create({
    dismissable: true,
    className: 'max-w-[600px]',
    children: ({ close }) => (
      <BlockConfirmDialog
        type={type}
        targetUsername={targetUsername}
        targetUserId={targetUserId}
        onComplete={choice => {
          close()
          onComplete?.(choice)
        }}
      />
    ),
  })
}
